import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { Account } from "@shared/schema";
import type { SameCustomerMatch } from "@shared/accountNameMatch";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

/** Imported accounts carry `0000000<qb id>` until someone types a real number. */
export function hasRealPhone(phone: string | null | undefined): boolean {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length >= 10 && !/^0{6}/.test(digits);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: Account;
  /** Active standalone accounts the request could be added to. */
  candidates: Account[];
  suggestions: SameCustomerMatch<Account>[];
  onAdded: (account: Account) => void;
}

/**
 * "Add to an existing account" (Pitt, 2026-10-20): the requester is a customer
 * who already has an account here. Their phone replaces the account's, the
 * account's PIN is cleared so they set their own, and the request goes away —
 * no second account, no second QuickBooks customer.
 */
export function AddRequestToAccountDialog({ open, onOpenChange, request, candidates, suggestions, onAdded }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<Account | null>(null);

  const close = (next: boolean) => {
    if (!next) setTarget(null);
    onOpenChange(next);
  };

  const addMutation = useMutation({
    mutationFn: async (targetAccountId: number) => {
      const response = await apiRequest("POST", `/api/accounts/${request.id}/add-to-account`, { targetAccountId });
      return response.json() as Promise<{
        account: Account;
        notifications: { sms: "sent" | "failed" | "skipped"; email: "sent" | "failed" | "skipped" };
      }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      queryClient.removeQueries({ queryKey: ["/api/accounts", String(request.id)] });
      const n = data.notifications;
      const sent = [n.sms === "sent" ? "text" : null, n.email === "sent" ? "email" : null].filter(Boolean);
      const failed = [n.sms === "failed" ? "text" : null, n.email === "failed" ? "email" : null].filter(Boolean);
      toast({
        title: `Added to ${data.account.name}`,
        description: failed.length > 0
          ? `Done, but the ${failed.join(" and ")} could not be sent. Let the customer know another way.`
          : sent.length > 0
            ? `The customer has been told by ${sent.join(" and ")} to set their PIN and order.`
            : "No notification was sent — tell the customer to sign in with their phone and set a PIN.",
        variant: failed.length > 0 ? "destructive" : undefined,
      });
      close(false);
      onAdded(data.account);
    },
    onError: (error: any) => {
      toast({ title: "Could not add to that account", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const suggestedIds = new Set(suggestions.map(s => s.account.id));
  const others = candidates.filter(a => !suggestedIds.has(a.id));

  const row = (a: Account, reason?: SameCustomerMatch<Account>["reason"]) => (
    <CommandItem
      key={a.id}
      value={`${a.name} ${a.id}`}
      onSelect={() => setTarget(a)}
      data-testid={`option-add-to-account-${a.id}`}
    >
      <span className="truncate">{a.name}</span>
      <span className="ml-auto flex items-center gap-1 shrink-0">
        {reason === "same-name" && <Badge variant="secondary">same name</Badge>}
        {!hasRealPhone(a.phone) && <Badge variant="outline">no real phone</Badge>}
      </span>
    </CommandItem>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg">
        {!target ? (
          <>
            <DialogHeader>
              <DialogTitle>Add to an existing account</DialogTitle>
              <DialogDescription>
                Pick the account that {request.requesterName || "this buyer"} already orders under. Their phone goes on it — no new account, no new QuickBooks customer.
              </DialogDescription>
            </DialogHeader>
            <Command className="border rounded-md">
              <CommandInput placeholder="Search accounts…" data-testid="input-add-to-account-search" />
              <CommandList className="max-h-72">
                <CommandEmpty>No account matches.</CommandEmpty>
                {suggestions.length > 0 && (
                  <CommandGroup heading="Likely the same customer">
                    {suggestions.map(s => row(s.account, s.reason))}
                  </CommandGroup>
                )}
                <CommandGroup heading="All accounts">
                  {others.map(a => row(a))}
                </CommandGroup>
              </CommandList>
            </Command>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Add {request.requesterName || "this buyer"} to {target.name}?</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>
                    {target.name}'s phone becomes <strong>{request.phone}</strong>.
                    {hasRealPhone(target.phone) && (
                      <> The current number, <strong>{target.phone}</strong>, will no longer sign in.</>
                    )}
                  </p>
                  <p>Its PIN is cleared — the buyer sets their own the first time they sign in. Orders, prices and the QuickBooks link stay as they are.</p>
                  <p>The request "{request.name}" is removed.</p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setTarget(null)} disabled={addMutation.isPending}>
                Back
              </Button>
              <Button
                className="bg-[#3D8C40] hover:bg-green-700 text-white"
                onClick={() => addMutation.mutate(target.id)}
                disabled={addMutation.isPending}
                data-testid="button-confirm-add-to-account"
              >
                {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add &amp; notify
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
